<script lang="ts">
  import { onMount } from 'svelte'
  import { fade, slide } from 'svelte/transition'
  import { api, handleApiError } from '../lib/api'
  import { addToast } from '../lib/toast'
  import BotRegistrationForm from '../components/BotRegistrationForm.svelte'
  import ConfirmModal from '../components/ui/ConfirmModal.svelte'

  type RegistrationCode = {
    id: string
    code: string
    session_id: string
    created_by_user_id: string
    description?: string
    active: boolean
    created_at: string
    expires_at?: string
  }

  type DeviceRegistration = {
    id: string
    code_id: string
    chat_id: number
    username?: string
    first_name?: string
    last_name?: string
    registered_at: string
    last_notified_at?: string
    active: boolean
  }

  type Session = {
    id: string
    label: string
    type?: string
    connected?: boolean
  }

  let codes: RegistrationCode[] = []
  let sessions: Session[] = []
  let loading = false
  let creating = false

  // Modal state
  let showCreateModal = false
  let selectedSessionId = ''
  let formDescription = ''
  let formExpiresAt = ''

  // View devices modal
  let showDevicesModal = false
  let selectedCodeId = ''
  let devices: DeviceRegistration[] = []
  let loadingDevices = false

  // Send notification modal
  let showNotificationModal = false
  let notificationCode = ''
  let notificationMessage = ''
  let sendingNotification = false

  // Confirm delete modal
  let showDeleteConfirm = false
  let codeToDelete: RegistrationCode | null = null

  // Filter
  let filterSessionId = ''

  async function loadSessions() {
    try {
      sessions = await api.get('/api/v1/sessions')
      // Filtrar solo sesiones de tipo telegram_bot
      sessions = sessions.filter((s: Session) => s.type === 'telegram_bot')
      
      // Si hay sesiones y no hay una seleccionada, seleccionar la primera
      if (sessions.length > 0 && !selectedSessionId) {
        selectedSessionId = sessions[0].id
      }
    } catch (e) {
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
      sessions = []
    }
  }

  async function loadCodes() {
    loading = true
    try {
      const params = new URLSearchParams()
      if (filterSessionId) {
        params.append('session_id', filterSessionId)
      }
      
      const queryString = params.toString()
      const response = await api.get(`/api/v1/bot-registrations${queryString ? '?' + queryString : ''}`)
      // Asegurar que siempre sea un array
      codes = Array.isArray(response) ? response : []
    } catch (e) {
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
      codes = []
    } finally {
      loading = false
    }
  }

  async function createCode(event?: CustomEvent) {
    if (!selectedSessionId) {
      addToast('Por favor selecciona una sesión de bot', 'error')
      return
    }

    creating = true
    try {
      const payload: any = {
        session_id: selectedSessionId,
      }

      // Si el evento trae datos, usarlos; si no, usar los valores del formulario
      if (event?.detail) {
        if (event.detail.description) payload.description = event.detail.description
        if (event.detail.expiresAt) payload.expires_at = event.detail.expiresAt
      } else {
        if (formDescription.trim()) {
          payload.description = formDescription.trim()
        }
        if (formExpiresAt) {
          payload.expires_at = new Date(formExpiresAt).toISOString()
        }
      }

      const newCode = await api.post('/api/v1/bot-registrations', payload)
      addToast('Código creado exitosamente', 'success')
      showCreateModal = false
      formDescription = ''
      formExpiresAt = ''
      await loadCodes()
    } catch (e) {
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
    } finally {
      creating = false
    }
  }

  async function deleteCode(code: RegistrationCode) {
    try {
      await api.delete(`/api/v1/bot-registrations/${code.id}`)
      addToast('Código eliminado exitosamente', 'success')
      await loadCodes()
    } catch (e) {
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
    }
  }

  async function loadDevices(codeId: string) {
    loadingDevices = true
    try {
      devices = await api.get(`/api/v1/bot-registrations/${codeId}/devices`)
    } catch (e) {
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
      devices = []
    } finally {
      loadingDevices = false
    }
  }

  function openDevicesModal(code: RegistrationCode) {
    selectedCodeId = code.id
    showDevicesModal = true
    loadDevices(code.id)
  }

  async function sendNotification(code: string) {
    if (!notificationMessage.trim()) {
      addToast('Por favor ingresa un mensaje', 'error')
      return
    }

    sendingNotification = true
    try {
      await api.post(`/api/v1/bot-registrations/${code}/notify`, {
        message: notificationMessage.trim(),
      })
      addToast('Notificación enviada exitosamente', 'success')
      showNotificationModal = false
      notificationMessage = ''
    } catch (e) {
      const errorMsg = handleApiError(e)
      addToast(errorMsg, 'error')
    } finally {
      sendingNotification = false
    }
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('es-ES')
  }

  function isExpired(code: RegistrationCode): boolean {
    if (!code.expires_at) return false
    return new Date(code.expires_at) < new Date()
  }

  function getSessionLabel(sessionId: string): string {
    const session = sessions.find((s) => s.id === sessionId)
    return session ? session.label : sessionId
  }

  $: filteredCodes = filterSessionId
    ? codes.filter((c) => c.session_id === filterSessionId)
    : codes

  onMount(async () => {
    await loadSessions()
    await loadCodes()
  })

  $: if (filterSessionId) {
    loadCodes()
  }
</script>

<div class="space-y-6">
  <!-- Header -->
  <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
    <div>
      <h1 class="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300">
        Códigos de Registro de Bot
      </h1>
      <p class="text-indigo-200/60 text-sm mt-1">
        Gestiona los códigos de vinculación para que los usuarios se registren con el bot
      </p>
    </div>
    <button
      on:click={() => {
        if (!selectedSessionId && sessions.length > 0) {
          selectedSessionId = sessions[0].id
        }
        showCreateModal = true
      }}
      disabled={sessions.length === 0}
      class="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 hover:from-blue-400 hover:via-purple-400 hover:to-indigo-400 text-white font-bold shadow-lg shadow-blue-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      + Crear Código
    </button>
  </div>

  <!-- Filters -->
  <div class="bg-black/30 border border-white/10 rounded-2xl p-4">
    <div class="flex flex-col sm:flex-row gap-4">
      <div class="flex-1">
        <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
          Filtrar por Sesión
        </label>
        <select
          bind:value={filterSessionId}
          class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none"
        >
          <option value="">Todas las sesiones</option>
          {#each sessions as session}
            <option value={session.id}>{session.label} {session.connected ? '🟢' : '🔴'}</option>
          {/each}
        </select>
      </div>
    </div>
  </div>

  <!-- Codes List -->
  {#if loading}
    <div class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
    </div>
  {:else if filteredCodes.length === 0}
    <div class="bg-black/30 border border-white/10 rounded-2xl p-12 text-center">
      <p class="text-indigo-200/60 text-lg">No hay códigos de registro</p>
      <p class="text-indigo-200/40 text-sm mt-2">
        {#if filterSessionId}
          No hay códigos para esta sesión
        {:else}
          Crea tu primer código para comenzar
        {/if}
      </p>
    </div>
  {:else}
    <div class="grid gap-4">
      {#each filteredCodes as code (code.id)}
        <div
          transition:slide
          class="bg-black/30 border border-white/10 rounded-2xl p-6 hover:bg-black/40 transition-all"
        >
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div class="flex-1">
              <div class="flex items-center gap-3 mb-2">
                <h3 class="text-xl font-bold text-white">{code.code}</h3>
                {#if !code.active}
                  <span class="px-2 py-1 rounded-lg bg-red-500/20 text-red-300 text-xs font-bold">
                    Inactivo
                  </span>
                {/if}
                {#if isExpired(code)}
                  <span class="px-2 py-1 rounded-lg bg-yellow-500/20 text-yellow-300 text-xs font-bold">
                    Expirado
                  </span>
                {/if}
              </div>
              {#if code.description}
                <p class="text-indigo-200/70 text-sm mb-2">{code.description}</p>
              {/if}
              <div class="flex flex-wrap gap-4 text-sm text-indigo-200/60">
                <span>📱 Sesión: {getSessionLabel(code.session_id)}</span>
                <span>📅 Creado: {formatDate(code.created_at)}</span>
                {#if code.expires_at}
                  <span>⏰ Expira: {formatDate(code.expires_at)}</span>
                {/if}
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                on:click={() => openDevicesModal(code)}
                class="px-4 py-2 rounded-xl bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 transition-colors text-sm font-bold"
              >
                Ver Dispositivos
              </button>
              <button
                on:click={() => {
                  notificationCode = code.code
                  showNotificationModal = true
                }}
                class="px-4 py-2 rounded-xl bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors text-sm font-bold"
              >
                Enviar Notificación
              </button>
              <button
                on:click={() => {
                  codeToDelete = code
                  showDeleteConfirm = true
                }}
                class="px-4 py-2 rounded-xl bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 transition-colors text-sm font-bold"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- Create Modal -->
{#if showCreateModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    transition:fade
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden"
      transition:slide
    >
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>

      <h3 class="text-2xl font-black text-white mb-6">Crear Código de Registro</h3>

      <div class="mb-6">
        <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
          Sesión de Bot
        </label>
        <select
          bind:value={selectedSessionId}
          class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none"
          disabled={creating}
        >
          {#each sessions as session}
            <option value={session.id}>
              {session.label} {session.connected ? '🟢' : '🔴'}
            </option>
          {/each}
        </select>
      </div>

      <BotRegistrationForm
        bind:description={formDescription}
        bind:expiresAt={formExpiresAt}
        bind:loading={creating}
        on:submit={(e) => createCode(e)}
        on:cancel={() => {
          showCreateModal = false
          formDescription = ''
          formExpiresAt = ''
        }}
      />
    </div>
  </div>
{/if}

<!-- Devices Modal -->
{#if showDevicesModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    transition:fade
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-2xl p-8 relative overflow-hidden max-h-[90vh] overflow-y-auto"
      transition:slide
    >
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>

      <div class="flex justify-between items-center mb-6">
        <h3 class="text-2xl font-black text-white">Dispositivos Registrados</h3>
        <button
          on:click={() => {
            showDevicesModal = false
            selectedCodeId = ''
            devices = []
          }}
          class="text-indigo-200/60 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>

      {#if loadingDevices}
        <div class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      {:else if devices.length === 0}
        <div class="text-center py-12">
          <p class="text-indigo-200/60 text-lg">No hay dispositivos registrados</p>
          <p class="text-indigo-200/40 text-sm mt-2">
            Los usuarios pueden registrarse enviando /yape &lt;código&gt; al bot
          </p>
        </div>
      {:else}
        <div class="space-y-3">
          {#each devices as device (device.id)}
            <div class="bg-black/30 border border-white/10 rounded-xl p-4">
              <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    {#if device.first_name}
                      <span class="font-bold text-white">
                        {device.first_name} {device.last_name || ''}
                      </span>
                    {/if}
                    {#if device.username}
                      <span class="text-indigo-200/60 text-sm">@{device.username}</span>
                    {/if}
                  </div>
                  <div class="text-sm text-indigo-200/60">
                    <span>Chat ID: {device.chat_id}</span>
                    {#if device.registered_at}
                      <span class="ml-4">Registrado: {formatDate(device.registered_at)}</span>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}

<!-- Notification Modal -->
{#if showNotificationModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    transition:fade
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden"
      transition:slide
    >
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500"></div>

      <h3 class="text-2xl font-black text-white mb-6">Enviar Notificación</h3>

      <div class="mb-4">
        <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
          Código
        </label>
        <div class="px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white font-mono">
          {notificationCode}
        </div>
      </div>

      <div class="mb-6">
        <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
          Mensaje
        </label>
        <textarea
          bind:value={notificationMessage}
          placeholder="Escribe el mensaje a enviar a todos los dispositivos registrados..."
          rows="5"
          class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all outline-none placeholder-white/20 resize-none"
          disabled={sendingNotification}
        ></textarea>
      </div>

      <div class="flex gap-3">
        <button
          on:click={() => {
            showNotificationModal = false
            notificationCode = ''
            notificationMessage = ''
          }}
          disabled={sendingNotification}
          class="flex-1 px-6 py-3 rounded-xl bg-black/30 border border-white/10 text-white hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold"
        >
          Cancelar
        </button>
        <button
          on:click={() => sendNotification(notificationCode)}
          disabled={sendingNotification || !notificationMessage.trim()}
          class="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 hover:from-purple-400 hover:via-pink-400 hover:to-indigo-400 text-white font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sendingNotification ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Delete Confirm Modal -->
<ConfirmModal
  isOpen={showDeleteConfirm}
  title="Eliminar Código"
  message="¿Estás seguro de que deseas eliminar este código? Esta acción no se puede deshacer."
  confirmText="Eliminar"
  cancelText="Cancelar"
  type="danger"
  on:confirm={() => {
    if (codeToDelete) {
      deleteCode(codeToDelete)
      codeToDelete = null
    }
    showDeleteConfirm = false
  }}
  on:cancel={() => {
    codeToDelete = null
    showDeleteConfirm = false
  }}
/>

